import React, { createContext, useContext, useState, useEffect } from 'react';
import ProductService from '../services/productService';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load initial data once
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const { products: productsData, categories: categoriesData } = await ProductService.loadInitialData();
      
      setProducts(productsData);
      setCategories(categoriesData);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Refresh data when needed
  const refreshData = async () => {
    ProductService.clearCache();
    await loadInitialData();
  };

  // Get product by ID (with caching)
  const getProduct = async (id) => {
    // First check if we have it in memory
    const existingProduct = products.find(p => p.id === id);
    if (existingProduct) {
      return existingProduct;
    }

    // Otherwise fetch from cache/API
    const { data } = await ProductService.getProductById(id);
    return data;
  };

  const value = {
    products,
    categories,
    loading,
    lastUpdated,
    refreshData,
    getProduct,
    // Helper functions
    getProductsByCategory: (categoryName) => 
      products.filter(p => p.categories?.name === categoryName),
    searchProducts: (query) => 
      products.filter(p => 
        (p.name || '').toLowerCase().includes(query.toLowerCase()) ||
        (p.description || '').toLowerCase().includes(query.toLowerCase())
      )
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};